import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Command } from 'lucide-react';
import { searchIndex, SearchItem } from '../../data/searchIndex';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-blue-50 text-blue-700 border border-blue-200',
  POST: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  PUT: 'bg-amber-50 text-amber-700 border border-amber-200',
  DELETE: 'bg-red-50 text-red-700 border border-red-200',
  PATCH: 'bg-purple-50 text-purple-700 border border-purple-200',
};

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return searchIndex.filter(
      item =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.url.toLowerCase().includes(q) ||
        item.method.toLowerCase() === q
    );
  }, [query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const grouped = useMemo(() => {
    const groups: Record<string, SearchItem[]> = {};
    results.forEach(item => {
      if (!groups[item.section]) groups[item.section] = [];
      groups[item.section].push(item);
    });
    return groups;
  }, [results]);

  const handleSelect = (item: SearchItem) => {
    navigate(`${item.path}`);
    onClose();
    setTimeout(() => {
      const el = document.getElementById(item.id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results.length > 0) {
        handleSelect(results[selectedIndex]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, results, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-150">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 border-b border-gray-100">
          <Search className="h-4 w-4 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search APIs and endpoints..."
            className="flex-1 py-4 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none"
          />
          {query ? (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 p-1 rounded">
              <X className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="hidden sm:inline text-xs text-gray-400 bg-gray-100 border border-gray-200 px-1.5 py-1 rounded font-mono">ESC</kbd>
          )}
        </div>

        {/* Body */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto">
          {!query.trim() ? (
            <div className="py-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">Search all API endpoints</p>
              <p className="text-xs text-gray-400">
                {searchIndex.length} endpoints across {new Set(searchIndex.map(i => i.section)).size} sections
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm font-medium text-gray-600 mb-1">
                No results for &ldquo;<span className="text-gray-900">{query}</span>&rdquo;
              </p>
              <p className="text-xs text-gray-400">Try searching by method (GET, POST), endpoint name, or URL path</p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(grouped).map(([section, items]) => (
                <div key={section}>
                  <div className="px-4 pt-3 pb-1.5">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{section}</span>
                  </div>
                  {items.map(item => {
                    const idx = results.indexOf(item);
                    const isSelected = idx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        data-index={idx}
                        onClick={() => handleSelect(item)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isSelected ? 'bg-[#581689]/5' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center w-12 shrink-0 px-0 py-0.5 rounded text-[10px] font-bold border ${
                            METHOD_COLORS[item.method] ?? 'bg-gray-50 text-gray-600 border-gray-200'
                          }`}
                        >
                          {item.method}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#581689]' : 'text-gray-900'}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{item.url}</p>
                        </div>
                        <ArrowRight className={`h-3.5 w-3.5 shrink-0 transition-colors ${isSelected ? 'text-[#581689]' : 'text-gray-300'}`} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-3 text-[11px] text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="bg-gray-100 border border-gray-200 px-1 py-0.5 rounded font-mono text-[10px]">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-gray-100 border border-gray-200 px-1 py-0.5 rounded font-mono text-[10px]">↵</kbd> open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-gray-100 border border-gray-200 px-1 py-0.5 rounded font-mono text-[10px]">ESC</kbd> close
            </span>
            <span className="ml-auto text-gray-400">{results.length} result{results.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
};
